import * as React from 'react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import FormProvider from 'src/components/hook-form/form-provider';
import { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useChangePassword } from 'src/api/auth';
import { useSnackbar } from 'src/components/snackbar';

type Props = {
  open: boolean;
  onClose: () => void;
};

type FormValues = {
  current_password: string;
  new_password: string;
  confirm_new_password: string;
};

const schema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup
    .string()
    .min(3, 'New password must be at least 3 characters')
    .required('New password is required'),
  confirm_new_password: yup
    .string()
    .oneOf([yup.ref('new_password')], 'Passwords do not match')
    .required('Confirm your new password'),
});

export default function ChangePasswordModal({ open, onClose }: Props) {
  const defaultValues = useMemo<FormValues>(() => ({
    current_password: '',
    new_password: '',
    confirm_new_password: '',
  }), []);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const { reset, handleSubmit, setError } = methods;
  const { enqueueSnackbar } = useSnackbar();
  const { changePassword } = useChangePassword();

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const submit = handleSubmit(async (data) => {
    try {
      await changePassword({ current_password: data.current_password, new_password: data.new_password });
      enqueueSnackbar('Password updated successfully', { variant: 'success' });
      reset(defaultValues);
      onClose();
    } catch (err: any) {
      const errors = err?.errors;
      if (Array.isArray(errors)) {
        const currentPwdErr = errors.find((e: any) => e?.attr === 'current_password');
        if (currentPwdErr?.detail) {
          setError('current_password', { type: 'server', message: currentPwdErr.detail });
          return;
        }
      }
      enqueueSnackbar(err?.message || 'Failed to change password', { variant: 'error' });
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <FormProvider methods={methods} onSubmit={submit}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <RHFTextField
              name="current_password"
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      <Iconify icon={showCurrentPassword ? 'eva:eye-off-fill' : 'eva:eye-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <RHFTextField
              name="new_password"
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      <Iconify icon={showNewPassword ? 'eva:eye-off-fill' : 'eva:eye-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <RHFTextField
              name="confirm_new_password"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      <Iconify icon={showConfirmPassword ? 'eva:eye-off-fill' : 'eva:eye-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={methods.formState.isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={methods.formState.isSubmitting}>
            Confirm
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}


